import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Autocomplete,
  Paper,
  Typography,
  Chip,
  Stack,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  InputAdornment,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  Search,
  FilterList,
  Close,
  CalendarMonth,
  Person,
  Receipt,
  KeyboardArrowDown,
  History,
  Numbers,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AInvoice } from '../../utils/invoice.types';
import CustomizedEstimateMenus from '../dropdownbutton/estimateActionMenu';
import { VocuherCategory } from '../../utils/common-utils';

interface InvoiceNavigationBarProps {
  currentInvoiceId: string;
  voucherCategory: string;
  voucherType: string;
  onInvoiceChange: (invoiceId: string, newTemplateId: string, vCategory: string) => void;
  invoices?: AInvoice[];
  currentInvoiceDetails?: any;
  onActionChange?: (data: any) => void;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  loadingMore?: boolean;
  onDirectSearch?: (invoiceNumber: string) => Promise<boolean>;
}

export const InvoiceNavigationBar: React.FC<InvoiceNavigationBarProps> = ({
  currentInvoiceId,
  voucherCategory,
  voucherType,
  onInvoiceChange,
  invoices = [],
  currentInvoiceDetails,
  onActionChange,
  onLoadMore,
  hasMoreData = false,
  loadingMore = false,
  onDirectSearch,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [recentDrawerOpen, setRecentDrawerOpen] = useState(false);
  const [directSearchOpen, setDirectSearchOpen] = useState(false);
  const [directSearchValue, setDirectSearchValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState<AInvoice[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [partyFilter, setPartyFilter] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  useEffect(() => {
    // Find current invoice index
    const index = invoices.findIndex(inv => inv._id === currentInvoiceId);
    setCurrentIndex(index);
  }, [currentInvoiceId, invoices]);

  useEffect(() => {
    // Apply filters
    let filtered = [...invoices];
    
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }
    
    if (dateFrom) {
      filtered = filtered.filter(inv => new Date(inv.date) >= dateFrom);
    }
    
    if (dateTo) {
      filtered = filtered.filter(inv => new Date(inv.date) <= dateTo);
    }
    
    if (partyFilter) {
      filtered = filtered.filter(inv => {
        const partyName = inv.buyer?.name || inv.supplier?.name || '';
        return partyName.toLowerCase().includes(partyFilter.toLowerCase());
      });
    }
    
    if (amountMin) {
      filtered = filtered.filter(inv => Number(inv.amount) >= parseFloat(amountMin));
    }
    
    if (amountMax) {
      filtered = filtered.filter(inv => Number(inv.amount) <= parseFloat(amountMax));
    }
    
    setFilteredInvoices(filtered);
  }, [invoices, statusFilter, dateFrom, dateTo, partyFilter, amountMin, amountMax]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const filterdInvoiceData = filteredInvoices[currentIndex - 1]
      onInvoiceChange(filterdInvoiceData._id, filterdInvoiceData.pdfTemplate, filterdInvoiceData.voucherCategory || "" );
    }
  };

  const handleNext = () => {
    // Check if we need to load more data when approaching the end
    const threshold = Math.floor(filteredInvoices.length * 0.8); // Load more at 80% of current data
    if (currentIndex >= threshold && hasMoreData && onLoadMore && !loadingMore) {
      onLoadMore();
    }
    
    if (currentIndex < filteredInvoices.length - 1) {
      const filterdInvoiceData = filteredInvoices[currentIndex + 1]
      onInvoiceChange(filterdInvoiceData._id, filterdInvoiceData.pdfTemplate, filterdInvoiceData.voucherCategory || "");
    }
  };
  
  const handleDirectSearch = async () => {
    if (onDirectSearch && directSearchValue.trim()) {
      const searchTerm = directSearchValue.trim();
      
      // Call parent's search function
      const success = await onDirectSearch(searchTerm);
      
      if (success) {
        setSearchQuery(searchTerm);
        setIsSearchMode(true);
        setDirectSearchOpen(false);
        setDirectSearchValue('');
      } else {
        // Search failed, don't enter search mode
        // Just close the search dialog
        setDirectSearchOpen(false);
        setDirectSearchValue('');
      }
    }
  };
  
  const clearSearchMode = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    // Reload normal invoice list
    window.location.reload(); // Simple way to reset to normal mode
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.altKey && event.key === 'ArrowLeft') {
      handlePrevious();
    } else if (event.altKey && event.key === 'ArrowRight') {
      handleNext();
    } else if (event.ctrlKey && event.key === 'f') {
      event.preventDefault();
      setSearchOpen(true);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex]);

  const getActiveFiltersCount = () => {
    let count = 0;
    if (statusFilter !== 'ALL') count++;
    if (dateFrom || dateTo) count++;
    if (partyFilter) count++;
    if (amountMin || amountMax) count++;
    return count;
  };

  return (
    <>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 1.5, 
          m: 0,
          border: '1px solid #e9ecf1',
          borderRadius: '12px',
          backgroundColor: '#fff'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative' }}>
          {/* Navigation Controls */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Previous Invoice (Alt + ←)">
              <IconButton 
                onClick={handlePrevious} 
                disabled={currentIndex <= 0}
                size="small"
              >
                <NavigateBefore />
              </IconButton>
            </Tooltip>
            
            <Stack sx={{ px: 1, minWidth: '150px', textAlign: 'center' }}>
              <Typography variant="body2">
                {currentIndex + 1} of {filteredInvoices.length}
                {loadingMore && <CircularProgress size={12} sx={{ ml: 1 }} />}
                {hasMoreData && !loadingMore && !isSearchMode && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' +'}  
                  </Typography>
                )}
              </Typography>
              {isSearchMode && (
                <Chip 
                  label={`Search: "${searchQuery}"`} 
                  size="small" 
                  onDelete={clearSearchMode}
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
            
            <Tooltip title="Next Invoice (Alt + →)">
              <IconButton 
                onClick={handleNext} 
                disabled={currentIndex >= filteredInvoices.length - 1}
                size="small"
              >
                <NavigateNext />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Quick Search */}
          <Autocomplete
            sx={{ minWidth: 250 }}
            size="small"
            open={searchOpen}
            onOpen={() => setSearchOpen(true)}
            onClose={() => setSearchOpen(false)}
            options={filteredInvoices}
            getOptionLabel={(option) => {
              const partyName = option.buyer?.name || option.supplier?.name || 'Unknown';
              return `${option.invoiceNumber || 'N/A'} - ${partyName}`;
            }}
            renderOption={(props, option) => {
              const partyName = option.buyer?.name || option.supplier?.name || 'Unknown';
              return (
                <Box component="li" {...props}>
                  <Stack>
                    <Typography variant="body2">{option.invoiceNumber || 'N/A'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {partyName} • ₹{Number(option.amount).toLocaleString('en-IN')}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search loaded invoices"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            onChange={(_, value) => {
              if (value) {
                onInvoiceChange(value._id, value.pdfTemplate, value.voucherCategory || "");
              }
            }}
          />
          
          {/* Direct Invoice Number Search */}
          {onDirectSearch && (
            <>
              {/* Search Toggle Button */}
              <Tooltip title="Search by Invoice Number">
                <IconButton 
                  onClick={() => setDirectSearchOpen(!directSearchOpen)} 
                  size="small"
                  sx={{ 
                    backgroundColor: directSearchOpen ? 'primary.main' : 'transparent',
                    color: directSearchOpen ? 'white' : 'inherit',
                    '&:hover': {
                      backgroundColor: directSearchOpen ? 'primary.dark' : 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <Numbers />
                </IconButton>
              </Tooltip>

              {/* Inline Search Field */}
              {directSearchOpen && (
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    mt: 1,
                    zIndex: 1300,
                    minWidth: 320
                  }}
                >
                  <Paper 
                    elevation={8}
                    sx={{ 
                      p: 2,
                      border: '1px solid #e9ecf1',
                      borderRadius: '12px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Numbers fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
                          Search Invoice
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setDirectSearchOpen(false);
                            setDirectSearchValue('');
                          }}
                          sx={{ ml: 'auto' }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter invoice number or partial match..."
                        value={directSearchValue}
                        onChange={(e) => setDirectSearchValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleDirectSearch();
                          }
                          if (e.key === 'Escape') {
                            setDirectSearchOpen(false);
                            setDirectSearchValue('');
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          sx: { 
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: 2
                              }
                            }
                          }
                        }}
                        autoFocus
                      />

                      <Stack direction="row" spacing={1}>
                        <Button 
                          variant="contained" 
                          onClick={handleDirectSearch}
                          disabled={!directSearchValue.trim()}
                          size="small"
                          sx={{ 
                            textTransform: 'none',
                            borderRadius: '8px',
                            flex: 1
                          }}
                        >
                          Search Invoices
                        </Button>
                        <Button 
                          variant="outlined" 
                          onClick={() => {
                            setDirectSearchOpen(false);
                            setDirectSearchValue('');
                          }}
                          size="small"
                          sx={{ 
                            textTransform: 'none',
                            borderRadius: '8px'
                          }}
                        >
                          Cancel
                        </Button>
                      </Stack>

                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        💡 Tip: Press Enter to search, Escape to close
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              )}
            </>
          )}

          {/* Filter Button */}
          <Tooltip title="Filter Invoices">
            <IconButton onClick={() => setFilterDrawerOpen(true)}>
              <Badge badgeContent={getActiveFiltersCount()} color="primary">
                <FilterList />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Recent Invoices */}
          <Tooltip title="Recent Invoices">
            <IconButton onClick={() => setRecentDrawerOpen(true)}>
              <History />
            </IconButton>
          </Tooltip>

          {/* Action Menu */}
          {onActionChange && currentInvoiceId && (
            <>
              <Divider orientation="vertical" flexItem />
              <CustomizedEstimateMenus 
                type={voucherType === 'sale' || voucherType === 'sales' ? VocuherCategory.SALE : 
                      voucherType === 'purchase' || voucherType === 'purchases' ? VocuherCategory.PURCHASE : VocuherCategory.SALE} 
                row={currentInvoiceDetails || { 
                  _id: currentInvoiceId, 
                  voucherType: voucherType === 'sale' || voucherType === 'sales' ? VocuherCategory.SALE : 
                               voucherType === 'purchase' || voucherType === 'purchases' ? VocuherCategory.PURCHASE : VocuherCategory.SALE,
                  invoiceNumber: currentInvoiceDetails?.invoiceNumber || 'Loading...'
                }} 
                actionChangeHandler={onActionChange} 
                showInlineAction={true}
              />
            </>
          )}
        </Stack>
      </Paper>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Filter Invoices</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Stack>

          <Stack spacing={3}>
            {/* Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SENT">Sent</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
                <MenuItem value="OVERDUE">Overdue</MenuItem>
              </Select>
            </FormControl>

            {/* Date Range */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={dateFrom}
                onChange={(newValue: any) => setDateFrom(newValue)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
              <DatePicker
                label="To Date"
                value={dateTo}
                onChange={(newValue: any) => setDateTo(newValue)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>

            {/* Party Filter */}
            <TextField
              size="small"
              fullWidth
              label="Party Name"
              value={partyFilter}
              onChange={(e) => setPartyFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Amount Range */}
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                label="Min Amount"
                type="number"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
              />
              <TextField
                size="small"
                label="Max Amount"
                type="number"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
              />
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setStatusFilter('ALL');
                  setDateFrom(null);
                  setDateTo(null);
                  setPartyFilter('');
                  setAmountMin('');
                  setAmountMax('');
                }}
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setFilterDrawerOpen(false)}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      {/* Recent Invoices Drawer */}
      <Drawer
        anchor="right"
        open={recentDrawerOpen}
        onClose={() => setRecentDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Recent Invoices</Typography>
            <IconButton onClick={() => setRecentDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Stack>

          <List>
            {filteredInvoices.slice(0, 10).map((invoice, index) => (
              <React.Fragment key={invoice._id}>
                <ListItemButton
                  selected={invoice._id === currentInvoiceId}
                  onClick={() => {
                    onInvoiceChange(invoice._id, invoice.pdfTemplate, invoice.voucherCategory || "");
                    setRecentDrawerOpen(false);
                  }}
                >
                  <ListItemText
                    primary={invoice.invoiceNumber || 'N/A'}
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          {invoice.buyer?.name || invoice.supplier?.name || 'Unknown'}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip 
                            label={`₹${Number(invoice.amount).toLocaleString('en-IN')}`} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                          <Chip 
                            label={invoice.status} 
                            size="small"
                            color={invoice.status === 'PAID' ? 'success' : 'default'}
                          />
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItemButton>
                {index < 9 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default InvoiceNavigationBar;